import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Database, SearchX, FileBarChart } from 'lucide-react';
import Loader from '../../components/Loader/Loader';
import { selectOffices, fetchOffices } from '../../features/offices/officesSlice';
import { selectAllDealers, fetchDealers } from '../../features/dealers/dealerSlice';
import {
  fetchOfficeInvoices, fetchOfficeClients, fetchOfficeDevices,
  fetchUniqueDevice, fetchEepromLogs, fetchDealerUpdates,
  clearReportsData
} from '../../features/reports/reportsSlice';
import { fetchDevices, selectAllDevices } from '../../features/devices/deviceSlice';
import './reports.css';
import { officeAPI } from '../../features/offices/officesAPI';

const getRecordTime = (record) => record?.rtime ?? record?.rTime ?? record?.time ?? record?.timestamp ?? record?.eventTime ?? record?.eventtime ?? record?.recordTime ?? record?.recordtime ?? record?.failTime ?? record?.failtime ?? record?.bypassTime ?? record?.bypasstime ?? record?.logtime ?? record?.dateTime ?? record?.datetime ?? record?.createdAt ?? record?.created_at;

const parseGmtDate = (value) => {
  if (!value) return null;
  const rawValue = String(value).trim();
  const usDateMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (usDateMatch) {
    const [, month, day, year, hour, minute, second = '0'] = usDateMatch;
    return new Date(Date.UTC(year, Number(month) - 1, day, hour, minute, second));
  }
  const slashDateMatch = rawValue.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (slashDateMatch) {
    const [, year, month, day, hour, minute, second = '0'] = slashDateMatch;
    return new Date(Date.UTC(year, Number(month) - 1, day, hour, minute, second));
  }
  const normalizedValue = !/(z|[+-]\d{2}:?\d{2})$/i.test(rawValue) && /^\d{4}-\d{2}-\d{2}/.test(rawValue)
    ? `${rawValue.replace(' ', 'T')}Z`
    : rawValue;
  const date = new Date(normalizedValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatIndiaDateTime = (value) => {
  const date = parseGmtDate(value);
  if (!date) return 'N/A';

  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(date);
};

const newestFirst = (records) => [...records].sort((a, b) => (parseGmtDate(getRecordTime(b))?.getTime() || 0) - (parseGmtDate(getRecordTime(a))?.getTime() || 0));

const formatDaysAndHours = (value) => {
  const daysValue = Number(value);
  if (Number.isFinite(daysValue) && daysValue >= 0) {
    const totalHours = Math.floor(daysValue * 24);
    return `${Math.floor(totalHours / 24)} day(s) and ${totalHours % 24} hour(s)`;
  }
  const text = String(value || '');
  const days = Number(text.match(/(\d+)\s*day/i)?.[1]);
  const hours = Number(text.match(/(\d+)\s*hour/i)?.[1]);
  if (!Number.isFinite(days) && !Number.isFinite(hours)) return 'N/A';
  const totalHours = (Number.isFinite(days) ? days * 24 : 0) + (Number.isFinite(hours) ? hours : 0);
  return `${Math.floor(totalHours / 24)} day(s) and ${totalHours % 24} hour(s)`;
};

const InlineSearchSelect = ({ value, placeholder, options, onChange, onSelect, emptyLabel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(value.toLowerCase()));

  return (
    <div className="inline-search-select">
      <input
        className="filter-select"
        placeholder={placeholder}
        value={value}
        onFocus={() => setIsOpen(true)}
        onChange={(event) => { onChange(event.target.value); setIsOpen(true); }}
        autoComplete="off"
      />
      {value && <button type="button" className="search-clear-button" aria-label="Clear selection" onClick={() => { onChange(''); setIsOpen(false); }}>×</button>}
      {isOpen && (
        <div className="inline-search-options" role="listbox">
          {filteredOptions.length ? filteredOptions.map((option) => (
            <button key={option.value} type="button" role="option" onMouseDown={(event) => event.preventDefault()} onClick={() => { onSelect(option); setIsOpen(false); }}>
              {option.label}
            </button>
          )) : <div className="inline-search-empty">{emptyLabel || 'No matching options'}</div>}
        </div>
      )}
    </div>
  );
};

const Reports = () => {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState('sales');
  const [activeBugSubTab, setActiveBugSubTab] = useState('no-gps');
  const [failRecordsPage, setFailRecordsPage] = useState(1);
  const failRecordsPerPage = 10;
  const [selectedDealer, setSelectedDealer] = useState('');
  const [selectedOffice, setSelectedOffice] = useState('');

  const [eventSno, setEventSno] = useState('');
  const [eventCbid, setEventCbid] = useState('');
  const [eventInstallDate, setEventInstallDate] = useState('undefined');

  // Search filter states
  const [officeSearchTerm, setOfficeSearchTerm] = useState('');
  const [dealerSearchTerm, setDealerSearchTerm] = useState('');
  const [deviceSearchTerm, setDeviceSearchTerm] = useState('');

  // Search input values (for controlled inputs)
  const [officeSearchValue, setOfficeSearchValue] = useState('');
  const [dealerSearchValue, setDealerSearchValue] = useState('');
  const [deviceSearchValue, setDeviceSearchValue] = useState('');

  // Report All states
  const [reportAllSearchType, setReportAllSearchType] = useState('office'); // 'office', 'dealer', 'sno'
  const [reportAllSelectedId, setReportAllSelectedId] = useState('');
  const [reportAllSearchTerm, setReportAllSearchTerm] = useState('');

  const user = useSelector(state => state.auth.user);
  const isAdmin = ['admin', 'superadmin'].includes(String(user?.role || '').toLowerCase());
  const storedOffices = useSelector(selectOffices) || [];
  const storedDealers = useSelector(selectAllDealers) || [];
  const [dealerOffices, setDealerOffices] = useState({ dealerId: null, offices: [] });
  const officesList = isAdmin ? storedOffices : String(dealerOffices.dealerId) === String(user?.dealerId) ? dealerOffices.offices : [];
  const dealersList = isAdmin ? storedDealers : user?.dealerId ? [{ dealerId: user.dealerId, dealerName: user.dealerName || user.userName || 'My dealership' }] : [];
  const allDevices = useSelector(selectAllDevices) || [];
  const reportsState = useSelector(state => state.reports);

  useEffect(() => {
    let cancelled = false;
    dispatch(clearReportsData());
    setSelectedOffice('');
    setSelectedDealer('');
    setOfficeSearchValue('');
    setDealerSearchValue('');
    setDealerOffices({ dealerId: null, offices: [] });
    if (isAdmin) {
      dispatch(fetchOffices());
      dispatch(fetchDealers());
    } else if (user?.dealerId) {
      officeAPI.fetchDealerOffices(user.dealerId).then(offices => {
        if (!cancelled) setDealerOffices({ dealerId: user.dealerId, offices: Array.isArray(offices) ? offices : [] });
      }).catch(() => {
        if (!cancelled) setDealerOffices({ dealerId: user.dealerId, offices: [] });
      });
    }
    dispatch(fetchDevices());
    return () => { cancelled = true; };
  }, [dispatch, isAdmin, user?.dealerId]);

  const handleDealerSelect = (e) => {
    const val = e.target.value;
    if (val && !dealersList.some(dealer => String(dealer.id || dealer.dealerId) === String(val))) return;
    setSelectedDealer(val);
    if (val && activeTab === 'bug') {
      dispatch(fetchDealerUpdates(val));
    }
  };

  const handleOfficeSelect = (e) => {
    const val = e.target.value;
    if (val && !officesList.some(office => String(office.id || office.officeId) === String(val))) return;
    setSelectedOffice(val);
    if (val) {
      if (activeTab === 'sales') dispatch(fetchOfficeInvoices(val));
      if (activeTab === 'clients') dispatch(fetchOfficeClients(val));
      if (activeTab === 'devices') dispatch(fetchOfficeDevices(val));
    }
  };

  useEffect(() => {
    if (selectedOffice) {
      if (activeTab === 'sales') dispatch(fetchOfficeInvoices(selectedOffice));
      if (activeTab === 'clients') dispatch(fetchOfficeClients(selectedOffice));
      if (activeTab === 'devices') dispatch(fetchOfficeDevices(selectedOffice));
    }
    if (selectedDealer && activeTab === 'bug') {
      dispatch(fetchDealerUpdates(selectedDealer));
    }
  }, [activeTab]); // Removed selectedOffice and selectedDealer to avoid double fetching on select

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    dispatch(clearReportsData());
    setSelectedOffice('');
    setSelectedDealer('');
    setEventSno('');
    setEventCbid('');
    setOfficeSearchTerm('');
    setDealerSearchTerm('');
    setDeviceSearchTerm('');
    setReportAllSearchTerm('');
    setOfficeSearchValue('');
    setDealerSearchValue('');
    setDeviceSearchValue('');
    setFailRecordsPage(1);
  };

  const tabs = [
    { id: 'sales', label: 'SALES REPORT', icon: '📊' },
    { id: 'clients', label: 'CLIENTS REPORT', icon: '👥' },
    { id: 'devices', label: 'DEVICE REPORT', icon: '📱' },
    { id: 'events', label: 'EVENTS REPORT', icon: '📅' },
    { id: 'bug', label: 'BUG REPORT', icon: '🐛' }
  ];

  const bugSubTabs = [
    { id: 'no-gps', label: 'NO GPS UPDATES', icon: '📍' },
    { id: 'no-event', label: 'NO EVENT UPDATES', icon: '📅' },
    { id: 'fail-records', label: 'FAIL RECORDS', icon: '❌' },
    { id: 'bypass-records', label: 'BYPASS RECORDS', icon: '🔄' },
    { id: 'sensor-fail', label: 'SENSOR FAIL RECORDS', icon: '⚠️' }
  ];

  const renderBugSubTabContent = () => {
    if (!selectedDealer) {
      return (
        <div className="empty-state">
          <FileBarChart size={48} className="empty-icon" />
          <p className="empty-text">Please select a dealer from the above dropdown to view the device updates</p>
        </div>
      );
    }

    if (reportsState.bug.loading) {
      return <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}><Loader /></div>;
    }

    const bugData = reportsState.bug.data || {};

    switch (activeBugSubTab) {
      case 'no-gps':
        const noGps = bugData["No Gps Vehicles"] || [];
        return (
          <div className="subtab-content">
            <div className="stats-grid">
              <div className="stat-card stat-card-warning">
                <div className="stat-icon">📍</div>
                <div className="stat-info">
                  <span className="stat-label">NO GPS UPDATES</span>
                  <span className="stat-value">{noGps.length}</span>
                </div>
              </div>
            </div>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Serial No</th>
                    <th>Registration</th>
                    <th>Client</th>
                    <th>Days Without Update</th>
                  </tr>
                </thead>
                <tbody>
                  {noGps.length > 0 ? noGps.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.sno}</td>
                      <td>{item.regnum}</td>
                      <td>{item.client}</td>
                      <td>{item["No Gps Updates From days"]}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="empty-table-cell">
                        <div className="empty-table-content">
                          <SearchX size={32} className="empty-table-icon" />
                          <span>No update records found</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'no-event':
        const noEvents = bugData["No Events Vehicles"] || [];
        return (
          <div className="subtab-content">
            <div className="stats-grid">
              <div className="stat-card stat-card-info">
                <div className="stat-icon">📅</div>
                <div className="stat-info">
                  <span className="stat-label">NO EVENT UPDATES</span>
                  <span className="stat-value">{noEvents.length}</span>
                </div>
              </div>
            </div>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Serial No</th>
                    <th>Registration</th>
                    <th>Client</th>
                    <th>Days Without Update</th>
                  </tr>
                </thead>
                <tbody>
                  {noEvents.length > 0 ? noEvents.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.sno}</td>
                      <td>{item.regnum}</td>
                      <td>{item.client}</td>
                      <td>{formatDaysAndHours(item["No Events Updates From days"])}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="empty-table-cell">
                        <div className="empty-table-content">
                          <SearchX size={32} className="empty-table-icon" />
                          <span>No update records found</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'fail-records':
        const fails = newestFirst(bugData["Alcohol Got Fails"] || []).map((item) => ({
          ...item,
          regnum: item.regnum || 'N/A',
          testType: String(item.typeofevent) === '254'
            ? (String(item.randominitial) === '1' ? 'RANDOM TEST' : 'INITIAL TEST')
            : 'N/A',
          bacvalue: item.bacvalue || 'N/A',
          rtime: formatIndiaDateTime(getRecordTime(item))
        }));
        const totalFailPages = Math.max(1, Math.ceil(fails.length / failRecordsPerPage));
        const safeFailPage = Math.min(failRecordsPage, totalFailPages);
        const pagedFails = fails.slice((safeFailPage - 1) * failRecordsPerPage, safeFailPage * failRecordsPerPage);
        return (
          <div className="subtab-content">
            <div className="stats-grid">
              <div className="stat-card stat-card-danger">
                <div className="stat-icon">❌</div>
                <div className="stat-info">
                  <span className="stat-label">FAIL RECORDS</span>
                  <span className="stat-value">{fails.length}</span>
                </div>
              </div>
            </div>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Registration Number</th>
                    <th>Event</th>
                    <th>BAC VALUE</th>
                    <th>Fail Time</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFails.length > 0 ? pagedFails.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.regnum}</td>
                      <td>{item.testType}</td>
                      <td>{item.bacvalue}</td>
                      <td>{item.rtime}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="empty-table-cell">
                        <div className="empty-table-content">
                          <Database size={32} className="empty-table-icon" />
                          <span>No fail records found for this dealer</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {fails.length > failRecordsPerPage && <div className="report-pagination">
              <span>Showing {(safeFailPage - 1) * failRecordsPerPage + 1}–{Math.min(safeFailPage * failRecordsPerPage, fails.length)} of {fails.length}</span>
              <div>
                <button type="button" onClick={() => setFailRecordsPage((page) => Math.max(1, page - 1))} disabled={safeFailPage === 1}>Previous</button>
                <span>Page {safeFailPage} of {totalFailPages}</span>
                <button type="button" onClick={() => setFailRecordsPage((page) => Math.min(totalFailPages, page + 1))} disabled={safeFailPage === totalFailPages}>Next</button>
              </div>
            </div>}
          </div>
        );
      case 'bypass-records':
        const bypass = newestFirst(bugData["Bypass 5"] || []).map((item) => ({
          ...item,
          regnum: item.regnum || 'N/A',
          eventType: String(item.typeofevent) === '60'
            ? 'BYPASS START'
            : String(item.typeofevent) === '61'
              ? 'BYPASS STOP'
              : 'UNKNOWN',
          rtime: formatIndiaDateTime(getRecordTime(item))
        }));
        return (
          <div className="subtab-content">
            <div className="stats-grid">
              <div className="stat-card stat-card-success">
                <div className="stat-icon">🔄</div>
                <div className="stat-info">
                  <span className="stat-label">BYPASS RECORDS</span>
                  <span className="stat-value">{bypass.length}</span>
                </div>
              </div>
            </div>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Registration Number</th>
                    <th>Event</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {bypass.length > 0 ? bypass.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.regnum}</td>
                      <td>{item.eventType}</td>
                      <td>{item.rtime}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="3" className="empty-table-cell">
                        <div className="empty-table-content">
                          <Database size={32} className="empty-table-icon" />
                          <span>No bypass records found for this dealer</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'sensor-fail':
        const sensor = newestFirst(bugData["Sensor Got Fails"] || []).map((item) => ({
          ...item,
          regnum: item.regnum || 'N/A',
          rtime: formatIndiaDateTime(getRecordTime(item)),
          vbat: item.vbat || 'N/A'
        }));
        return (
          <div className="subtab-content">
            <div className="stats-grid">
              <div className="stat-card stat-card-primary">
                <div className="stat-icon">⚠️</div>
                <div className="stat-info">
                  <span className="stat-label">SENSOR FAIL RECORDS</span>
                  <span className="stat-value">{sensor.length}</span>
                </div>
              </div>
            </div>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Registration Number</th>
                    <th>Fail Time</th>
                    <th>Parameters</th>
                  </tr>
                </thead>
                <tbody>
                  {sensor.length > 0 ? sensor.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.regnum}</td>
                      <td>{item.rtime}</td>
                      <td>{item.vbat}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="3" className="empty-table-cell">
                        <div className="empty-table-content">
                          <Database size={32} className="empty-table-icon" />
                          <span>No sensor fail records found for this dealer</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'sales':
        const salesData = reportsState.sales.data || [];
        const totalRevenue = salesData.reduce((acc, curr) => acc + parseFloat(curr.total || 0), 0).toFixed(2);
        const amountPaid = salesData.reduce((acc, curr) => acc + parseFloat(curr.amountpaid || 0), 0).toFixed(2);

        return (
          <div className="report-content">
            <div className="filter-section">
              <div className="filter-group">
                <label className="filter-label">Select Office*</label>
                <InlineSearchSelect value={officeSearchValue} placeholder="Search & select office..." options={officesList.map(o => ({ value: o.id || o.officeId, label: o.officeName || o.office || '' }))} onChange={(value) => { setOfficeSearchValue(value); setSelectedOffice(''); }} onSelect={(office) => { setOfficeSearchValue(office.label); handleOfficeSelect({ target: { value: office.value } }); }} />
              </div>
            </div>

            {selectedOffice ? (
              reportsState.sales.loading ? (
                <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}><Loader /></div>
              ) : (
                <div className="stats-grid">
                  <div className="stat-card stat-card-primary">
                    <div className="stat-icon">💰</div>
                    <div className="stat-info">
                      <span className="stat-label">Total Billed</span>
                      <span className="stat-value">${totalRevenue}</span>
                    </div>
                  </div>
                  <div className="stat-card stat-card-success">
                    <div className="stat-icon">📈</div>
                    <div className="stat-info">
                      <span className="stat-label">Amount Paid</span>
                      <span className="stat-value">${amountPaid}</span>
                    </div>
                  </div>
                  <div className="stat-card stat-card-info">
                    <div className="stat-icon">🧾</div>
                    <div className="stat-info">
                      <span className="stat-label">Invoices</span>
                      <span className="stat-value">{salesData.length}</span>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="empty-state">
                <FileBarChart size={48} className="empty-icon" />
                <p className="empty-text">Please select an office to view the sales report</p>
              </div>
            )}
          </div>
        );

      case 'clients':
        const clientsData = reportsState.clients.data || [];
        return (
          <div className="report-content">
            <div className="filter-section">
              <div className="filter-group">
                <label className="filter-label">Select Office*</label>
                <InlineSearchSelect value={officeSearchValue} placeholder="Search & select office..." options={officesList.map(o => ({ value: o.id || o.officeId, label: o.officeName || o.office || '' }))} onChange={(value) => { setOfficeSearchValue(value); setSelectedOffice(''); }} onSelect={(office) => { setOfficeSearchValue(office.label); handleOfficeSelect({ target: { value: office.value } }); }} />
              </div>
            </div>

            {selectedOffice ? (
              reportsState.clients.loading ? (
                <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}><Loader /></div>
              ) : (
                <div className="clients-stats">
                  <div className="stats-grid-2col">
                    <div className="stat-card stat-card-info">
                      <div className="stat-icon">👥</div>
                      <div className="stat-info">
                        <span className="stat-label">Total Clients</span>
                        <span className="stat-value">{clientsData.length}</span>
                      </div>
                    </div>
                    <div className="stat-card stat-card-success">
                      <div className="stat-icon">✅</div>
                      <div className="stat-info">
                        <span className="stat-label">Active Clients</span>
                        <span className="stat-value">{clientsData.filter(c => c.status === 'Active' || c.status === 'Installed').length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="empty-state">
                <FileBarChart size={48} className="empty-icon" />
                <p className="empty-text">Please select an office to view the clients report</p>
              </div>
            )}
          </div>
        );

      case 'devices':
        const devicesData = reportsState.devices.data || [];
        return (
          <div className="report-content">
            <div className="filter-section">
              <div className="filter-group">
                <label className="filter-label">Select Office*</label>
                <InlineSearchSelect value={officeSearchValue} placeholder="Search & select office..." options={officesList.map(o => ({ value: o.id || o.officeId, label: o.officeName || o.office || '' }))} onChange={(value) => { setOfficeSearchValue(value); setSelectedOffice(''); }} onSelect={(office) => { setOfficeSearchValue(office.label); handleOfficeSelect({ target: { value: office.value } }); }} />
              </div>
            </div>

            {selectedOffice ? (
              reportsState.devices.loading ? (
                <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}><Loader /></div>
              ) : (
                <div className="devices-stats">
                  <div className="stats-grid-2col">
                    <div className="stat-card stat-card-primary">
                      <div className="stat-icon">📱</div>
                      <div className="stat-info">
                        <span className="stat-label">Total devices</span>
                        <span className="stat-value">{devicesData.length}</span>
                      </div>
                    </div>
                    <div className="stat-card stat-card-warning">
                      <div className="stat-icon">📦</div>
                      <div className="stat-info">
                        <span className="stat-label">Available devices</span>
                        <span className="stat-value">{devicesData.filter(d => d.status === 'Available').length}</span>
                      </div>
                    </div>
                    <div className="stat-card stat-card-info">
                      <div className="stat-icon">🔧</div>
                      <div className="stat-info">
                        <span className="stat-label">Devices went to service</span>
                        <span className="stat-value">{devicesData.filter(d => d.status === 'Service').length}</span>
                      </div>
                    </div>
                    <div className="stat-card stat-card-success">
                      <div className="stat-icon">✅</div>
                      <div className="stat-info">
                        <span className="stat-label">Installed devices</span>
                        <span className="stat-value">{devicesData.filter(d => d.status === 'Installed').length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="empty-state">
                <FileBarChart size={48} className="empty-icon" />
                <p className="empty-text">Please select an office to view the devices report</p>
              </div>
            )}
          </div>
        );

      case 'events':
        return (
          <div className="report-content">
            <div className="filter-section">
              <div className="filter-group">
                <label className="filter-label">Select Vehicle*</label>
                <InlineSearchSelect value={deviceSearchValue} placeholder="Search & select vehicle..." options={allDevices.map(d => ({ value: d.sno, label: d.dealer ? `${d.sno} — ${d.dealer}` : d.sno }))} onChange={(value) => { setDeviceSearchValue(value); setEventSno(''); }} onSelect={(device) => {
                  setDeviceSearchValue(device.label);
                  setEventSno(device.value);
                  dispatch(fetchUniqueDevice(device.value)).unwrap().then((res) => {
                          if (res && res.length > 0) {
                            const devInf = res[0];
                            const cbid = devInf.cbid || '';
                            const installDate = devInf.installDate || 'undefined';
                            setEventCbid(cbid);
                            setEventInstallDate(installDate);
                            dispatch(fetchEepromLogs({ cbid, installDate }));
                          }
                  }).catch(err => {
                    console.error("Failed to fetch device info:", err);
                  });
                }} />
              </div>
            </div>

            {reportsState.events.loading ? (
              <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}><Loader /></div>
            ) : (reportsState.events.deviceData || reportsState.events.logsData.length > 0) ? (
              <div className="events-stats">
                <div className="stats-grid" style={{ marginBottom: '24px' }}>
                  <div className="stat-card stat-card-primary">
                    <div className="stat-icon">📊</div>
                    <div className="stat-info">
                      <span className="stat-label">TOTAL TESTS</span>
                      <span className="stat-value">
                        {reportsState.events.logsData.filter(item => 
                          item.typeofevent === "65" || item.typeofevent == "254"
                        ).length}
                      </span>
                    </div>
                  </div>
                  <div className="stat-card stat-card-success">
                    <div className="stat-icon">✅</div>
                    <div className="stat-info">
                      <span className="stat-label">PASS TESTS</span>
                      <span className="stat-value">
                        {reportsState.events.logsData.filter(item => 
                          item.passfailstatus === "1" && item.typeofevent == "254"
                        ).length}
                      </span>
                    </div>
                  </div>
                  <div className="stat-card stat-card-danger">
                    <div className="stat-icon">❌</div>
                    <div className="stat-info">
                      <span className="stat-label">FAIL TESTS</span>
                      <span className="stat-value">
                        {reportsState.events.logsData.filter(item => 
                          item.passfailstatus === "0" && item.typeofevent == "254"
                        ).length}
                      </span>
                    </div>
                  </div>
                  <div className="stat-card stat-card-warning">
                    <div className="stat-icon">⚠️</div>
                    <div className="stat-info">
                      <span className="stat-label">INSUFFICIENT VOLUMES</span>
                      <span className="stat-value">
                        {reportsState.events.logsData.filter(item => 
                          item.typeofevent === "65"
                        ).length}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Additional mapping of data can go here based on API format */}
              </div>
            ) : (
              <div className="empty-state">
                <FileBarChart size={48} className="empty-icon" />
                <p className="empty-text">Enter serial number to view the events report</p>
              </div>
            )}
          </div>
        );

      case 'bug':
        return (
          <div className="report-content">
            <div className="filter-section">
              <div className="filter-group">
                <label className="filter-label">Select Dealer*</label>
                <InlineSearchSelect value={dealerSearchValue} placeholder="Search & select dealer..." options={dealersList.map(d => ({ value: d.id || d.dealerId, label: d.dealerName || '' }))} onChange={(value) => { setDealerSearchValue(value); setSelectedDealer(''); }} onSelect={(dealer) => { setDealerSearchValue(dealer.label); handleDealerSelect({ target: { value: dealer.value } }); }} />
              </div>
            </div>

            {selectedDealer && <div className="bug-subtabs-wrapper">
              {bugSubTabs.map((subtab) => (
                <button
                  key={subtab.id}
                  className={`bug-subtab-btn ${activeBugSubTab === subtab.id ? 'active' : ''}`}
                  onClick={() => { setActiveBugSubTab(subtab.id); setFailRecordsPage(1); }}
                >
                  <span className="subtab-icon">{subtab.icon}</span>
                  <span className="subtab-label">{subtab.label}</span>
                </button>
              ))}
            </div>}

            {selectedDealer && <div className="bug-subtab-content">
              {renderBugSubTabContent()}
            </div>}
          </div>
        );

      case 'report-all':
        return (
          <div className="report-content">
            <div className="filter-section" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="filter-group">
                <label className="filter-label">Search By</label>
                <select
                  className="filter-select"
                  value={reportAllSearchType}
                  onChange={(e) => {
                    setReportAllSearchType(e.target.value);
                    setReportAllSelectedId('');
                  }}
                >
                  <option value="office">Office</option>
                  <option value="dealer">Dealer</option>
                  <option value="sno">Device (SNO)</option>
                </select>
              </div>

              <div className="filter-group" style={{ flex: 1, minWidth: '200px' }}>
                <label className="filter-label">Select {reportAllSearchType.charAt(0).toUpperCase() + reportAllSearchType.slice(1)}</label>
                <div className="searchable-select-wrapper">
                  <input
                    type="text"
                    className="filter-search-input"
                    placeholder={`Search ${reportAllSearchType}...`}
                    value={reportAllSearchTerm}
                    onChange={(e) => setReportAllSearchTerm(e.target.value)}
                  />
                  <select
                    className="filter-select"
                    value={reportAllSelectedId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setReportAllSelectedId(val);
                      if (val) {
                        if (reportAllSearchType === 'office') {
                          dispatch(fetchOfficeInvoices(val));
                          dispatch(fetchOfficeClients(val));
                          dispatch(fetchOfficeDevices(val));
                        } else if (reportAllSearchType === 'dealer') {
                          dispatch(fetchDealerUpdates(val));
                        } else if (reportAllSearchType === 'sno') {
                          dispatch(fetchUniqueDevice(val)).unwrap().then((res) => {
                            if (res && res.length > 0) {
                              const devInf = res[0];
                              dispatch(fetchEepromLogs({
                                cbid: devInf.cbid || '',
                                installDate: devInf.installDate || 'undefined'
                              }));
                            }
                          }).catch(err => {
                            console.error("Failed to fetch device info for report all:", err);
                          });
                        }
                      }
                    }}
                  >
                    <option value="">Select {reportAllSearchType}</option>
                    {reportAllSearchType === 'office' && officesList.filter(o => (o.officeName || o.office || '').toLowerCase().includes(reportAllSearchTerm.toLowerCase())).map(o => (
                      <option key={o.id || o.officeId} value={o.id || o.officeId}>{o.officeName || o.office}</option>
                    ))}
                    {reportAllSearchType === 'dealer' && dealersList.filter(d => (d.dealerName || '').toLowerCase().includes(reportAllSearchTerm.toLowerCase())).map(d => (
                      <option key={d.id || d.dealerId} value={d.id || d.dealerId}>{d.dealerName}</option>
                    ))}
                    {reportAllSearchType === 'sno' && allDevices.filter(d => (d.sno || '').toLowerCase().includes(reportAllSearchTerm.toLowerCase())).map(d => (
                      <option key={d.sno} value={d.sno}>{d.sno}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {!reportAllSelectedId ? (
              <div className="empty-state">
                <SearchX size={48} className="empty-icon" />
                <p className="empty-text">Select an entity above to generate a comprehensive report</p>
              </div>
            ) : (
              <div className="report-all-results" style={{ marginTop: '30px' }}>
                {reportAllSearchType === 'office' && (
                  <div className="results-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                    <div className="result-section">
                      <h3>Sales Overview</h3>
                      {reportsState.sales.loading ? <Loader /> : (
                        <div className="mini-stats">
                          <p>Total Billed: ${reportsState.sales.data.reduce((acc, curr) => acc + parseFloat(curr.total || 0), 0).toFixed(2)}</p>
                          <p>Invoices: {reportsState.sales.data.length}</p>
                        </div>
                      )}
                    </div>
                    <div className="result-section">
                      <h3>Clients Overview</h3>
                      {reportsState.clients.loading ? <Loader /> : (
                        <div className="mini-stats">
                          <p>Total Clients: {reportsState.clients.data.length}</p>
                          <p>Active: {reportsState.clients.data.filter(c => c.status === 'Active' || c.status === 'Installed').length}</p>
                        </div>
                      )}
                    </div>
                    <div className="result-section">
                      <h3>Devices Overview</h3>
                      {reportsState.devices.loading ? <Loader /> : (
                        <div className="mini-stats">
                          <p>Total Devices: {reportsState.devices.data.length}</p>
                          <p>Installed: {reportsState.devices.data.filter(d => d.status === 'Installed').length}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {reportAllSearchType === 'dealer' && (
                  <div className="result-section">
                    <h3>Dealer Bug Reports</h3>
                    {reportsState.bug.loading ? <Loader /> : (
                      <div className="stats-grid">
                        <div className="stat-card stat-card-warning">
                          <span className="stat-label">No GPS Updates</span>
                          <span className="stat-value">{(reportsState.bug.data?.["No Gps Vehicles"] || []).length}</span>
                        </div>
                        <div className="stat-card stat-card-danger">
                          <span className="stat-label">Alcohol Fails</span>
                          <span className="stat-value">{(reportsState.bug.data?.["Alcohol Got Fails"] || []).length}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {reportAllSearchType === 'sno' && (
                  <div className="result-section">
                    <h3>Device & Events Summary</h3>
                    {reportsState.events.loading ? <Loader /> : (
                      <div className="stats-grid">
                        <div className="stat-card stat-card-primary">
                          <span className="stat-label">Device Data Fetched</span>
                          <span className="stat-value">{reportsState.events.deviceData ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="stat-card stat-card-success">
                          <span className="stat-label">Total Event Logs</span>
                          <span className="stat-value">{reportsState.events.logsData.length}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="reports-container">
      <div className="reports-card">
        <div className="reports-header">
          <div>
            <h1 className="reports-title">Reports Dashboard</h1>
            <p className="reports-subtitle">Comprehensive analytics and insights</p>
          </div>
        </div>

        <div className="tabs-wrapper">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="reports-content-wrapper">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Reports;
